import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

export type PermissionsFeedStatus =
  | 'idle' // never fetched (or cleared on logout)
  | 'loading' // request in flight
  | 'ready' // 200 with >= 1 grant
  | 'noAccess' // 403 (no Mobile root) OR 200 with an empty grant list
  | 'errorStale' // transport failure, previously loaded grants retained
  | 'errorCold'; // transport failure, never loaded -> no grants at all

interface AbpResponse<T> {
  result: T;
  success: boolean;
}

interface PermissionsResult {
  readonly grantedPermissionNames: readonly string[];
}

const SETTLED_NO_REFETCH: readonly PermissionsFeedStatus[] = ['ready', 'noAccess', 'errorStale'];

@Injectable({ providedIn: 'root' })
export class PermissionsService {
  private readonly http = inject(HttpClient);

  private readonly _grants = signal<readonly string[]>([]);
  private readonly _status = signal<PermissionsFeedStatus>('idle');
  private readonly _loadedOnce = signal(false);

  private inFlight: Promise<void> | null = null;
  private generation = 0;

  readonly loading = computed(() => this._status() === 'loading');
  readonly errorKind = computed<'stale' | 'cold' | null>(() =>
    this._status() === 'errorStale' ? 'stale' : this._status() === 'errorCold' ? 'cold' : null,
  );

  /**
   * True once the feed has settled into a decided outcome (`ready`/`noAccess`/`errorStale`/
   * `errorCold`) — false while `idle` or `loading`. Lets callers awaiting `ensureLoaded()`
   * detect a `clear()` that landed mid-flight (e.g. session expiry racing a deep-link): the
   * discarded response leaves the feed back at `idle` instead of a genuine decision.
   */
  readonly isSettled = computed(() => this._status() !== 'idle' && this._status() !== 'loading');

  /**
   * Idempotent: joins an in-flight request if one exists, no-ops (resolves immediately) if the
   * feed already settled into `ready | noAccess | errorStale`, otherwise starts a fetch. Called
   * by both the menu page and `permissionGuard` — safe to call from multiple places at once.
   */
  ensureLoaded(): Promise<void> {
    if (this.inFlight) return this.inFlight;
    if (SETTLED_NO_REFETCH.includes(this._status())) return Promise.resolve();
    return this.fetch();
  }

  /**
   * Menu error-banner action. Unlike `ensureLoaded()`, this bypasses the settled-state
   * short-circuit and always forces a fresh fetch — unless a request is already in flight, in
   * which case it joins that single request rather than issuing a second one.
   */
  retry(): Promise<void> {
    if (this.inFlight) return this.inFlight;
    return this.fetch();
  }

  /**
   * Resets to `idle` with empty grants (logout / tenant switch). Also invalidates any
   * in-flight fetch via the generation counter so a response for a pre-clear request can never
   * write state after this runs.
   */
  clear(): void {
    this.generation++;
    this.inFlight = null;
    this._grants.set([]);
    this._status.set('idle');
    this._loadedOnce.set(false);
  }

  hasPermission(code: string): boolean {
    return this._grants().includes(code);
  }

  private fetch(): Promise<void> {
    this._status.set('loading');
    const generationAtStart = this.generation;

    const promise = firstValueFrom(
      this.http
        .get<AbpResponse<PermissionsResult>>(`${environment.apiUrl}/api/services/app/Mobile/GetUserPermissions`)
        .pipe(map((r) => r.result?.grantedPermissionNames ?? [])),
    )
      .then((grants) => {
        if (generationAtStart !== this.generation) return;
        this._loadedOnce.set(true);
        if (grants.length === 0) {
          this._grants.set([]);
          this._status.set('noAccess');
        } else {
          this._grants.set([...grants]);
          this._status.set('ready');
        }
      })
      .catch((err: unknown) => {
        if (generationAtStart !== this.generation) return;
        if (err instanceof HttpErrorResponse && err.status === 403) {
          this._loadedOnce.set(true);
          this._grants.set([]);
          this._status.set('noAccess');
          return;
        }
        if (this._loadedOnce()) {
          this._status.set('errorStale');
        } else {
          this._grants.set([]);
          this._status.set('errorCold');
        }
      });

    this.inFlight = promise;
    void promise.finally(() => {
      if (this.inFlight === promise) this.inFlight = null;
    });
    return promise;
  }
}
